import React, { useState } from "react";
import { useScrollLock } from "../hooks/useScrollLock";
import styles from "../styles/HowToPlayPopup.module.css";

interface HowToPlayPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

interface AccordionSection {
  id: string;
  title: string;
  content: React.ReactNode;
  defaultOpen?: boolean;
}

const HowToPlayPopup: React.FC<HowToPlayPopupProps> = ({ isVisible, onClose }) => {
  const [openSection, setOpenSection] = useState<string | null>(""); // Default open section
  
  useScrollLock(isVisible);

  const toggleSection = (sectionId: string) => {
    if (openSection === sectionId) {
      // If clicking the already open section, close it
      setOpenSection(null);
    } else {
      // Open the clicked section (automatically closes any other open section)
      setOpenSection(sectionId);
    }
  };

  const accordionSections: AccordionSection[] = [
    {
      id: "dxf",
      title: "DeathX Fun Token",
      content: (
        <div className={styles.howtoplayReferral}>
          <p>DeathX Fun Token (DXF) is a play-to-earn token to reward early stage players:</p>
          <ul className={styles.howtoplayListInside}>
            <li>DXF token rewarded each game even when you lost</li>
            <li>2% of every bet goes to buying DXF from the market</li>
            <li>DXF tokens can be used in the gameplay to gain powerful advantages</li>
            <li>
              Join our community on Telegram{" "}
              <a href="https://t.me/DXFCommunity" target="_blank" rel="noreferrer" className={styles.howtoplayLink}>
                @DXFCommunity
              </a>{" "}
              to get random airdrops
            </li>
          </ul>
        </div>
      ),
    },
    {
      id: "provably-fair",
      title: "Provably Fair",
      content: (
        <div className={styles.howtoplayProvablyFair}>
          <p>Each game uses cryptographic hashing to ensure fairness:</p>
          <ul className={styles.howtoplayListInside}>
            <li>Game seed is generated before play and hashed</li>
            <li>Death tile positions are determined by the seed</li>
            <li>After game completion, you can verify using the seed</li>
          </ul>
        </div>
      ),
    },
    {
      id: "referral",
      title: "Referral Program",
      content: (
        <div className={styles.howtoplayReferral}>
          <p>Earn rewards by referring friends:</p>
          <ul className={styles.howtoplayListInside}>
            <li>Get 2% commission on referred users' total bet amount</li>
            <li>Minimum payout of 0.01 BNB</li>
            <li>Share your referral link from the REFERRAL menu</li>
          </ul>
        </div>
      ),
    },
    {
      id: "multiplier",
      title: "Multiplier Calculation",
      defaultOpen: true,
      content: (
        <div className={styles.howtoplayMultiplier}>
          <div className={styles.howtoplayCalcSection}>
            <p>Base multiplier per row:</p>
            <p className={styles.howtoplayFormula}>1 / (1 - death_probability)</p>
          </div>
          <div className={styles.howtoplayCalcSection}>
            <p>Example w/ 5 tiles:</p>
            <p className={styles.howtoplayFormula}>1 / (1 - 0.2) = 1.25x</p>
          </div>
          <div className={styles.howtoplayCalcSection}>
            <p>Each row multiplies with previous rows:</p>
            <p className={styles.howtoplayFormula}>Row 2 Total = Row 1 Base × Row 2 Base</p>
          </div>
          <div className={styles.howtoplayCalcSection}>
            <p>A 5% house edge is then applied:</p>
            <p className={styles.howtoplayFormula}>Row 2 Total × 0.9500 = Total Multiplier</p>
          </div>
        </div>
      ),
    },
    {
      id: "support",
      title: "Support",
      content: (
        <div className={styles.howtoplaySupport}>
          <p>Need help? Check out these resources:</p>
          <ul className={styles.howtoplayListInside}>
            <li>
              Visit our{" "}
              <a
                href="https://deathx-fun.notion.site/DeathX-fun-Docs-24e1b048608b80f69c33d28127a39c53"
                target="_blank"
                rel="noreferrer"
                className={styles.howtoplayLink}
              >
                documentation
              </a>{" "}
              for detailed guides
            </li>
            <li>
              Join our community on Telegram{" "}
              <a href="https://t.me/DXFCommunity" target="_blank" rel="noreferrer" className={styles.howtoplayLink}>
                @DXFCommunity
              </a>
            </li>
            <li>
              Follow us on X{" "}
              <a href="https://x.com/intent/follow?screen_name=ProofXGames" target="_blank" rel="noreferrer" className={styles.howtoplayLink}>
                @ProofXGames
              </a>{" "}
              for updates
            </li>
          </ul>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;

  return (
    <div className={styles.howtoplayOverlay} onClick={onClose}>
      <div
        className={styles.howtoplayDialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="howtoplay-title"
        aria-modal="true"
      >
        <div className={styles.howtoplayHeader}>
          <h2 id="howtoplay-title" className={styles.howtoplayTitle}>
            How to Play
          </h2>
        </div>

        <div className={styles.howtoplayContent}>
          <div className={styles.howtoplayBasicRules}>
            <ol className={styles.howtoplayRulesList}>
              <li>Place your bet</li>
              <li>Choose one tile per row</li>
              <li>Each row has one death tile that busts you to 0</li>
              <li>Your payout multiplier increases for each row you survive</li>
              <li>Cash out anytime</li>
            </ol>
          </div>

          <div className={styles.howtoplayAccordion}>
            {accordionSections.map((section) => {
              const isOpen = openSection === section.id;
              return (
                <div key={section.id} className={styles.howtoplayAccordionItem}>
                  <button
                    className={styles.howtoplayAccordionTrigger}
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={isOpen}
                    aria-controls={`howtoplay-content-${section.id}`}
                  >
                    <span className={styles.howtoplayAccordionTitle}>{section.title}</span>
                    <svg
                      className={`${styles.howtoplayAccordionIcon} ${isOpen ? styles.open : ""}`}
                      width="16"
                      height="16"
                      viewBox="0 0 256 256"
                      fill="currentColor"
                    >
                      <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" />
                    </svg>
                  </button>
                  <div
                    id={`howtoplay-content-${section.id}`}
                    className={`${styles.howtoplayAccordionContent} ${isOpen ? styles.open : ""}`}
                    role="region"
                    aria-labelledby={`howtoplay-trigger-${section.id}`}
                  >
                    <div className={styles.howtoplayAccordionBody}>{section.content}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button className={styles.howtoplayCloseButton} onClick={onClose} aria-label="Close dialog">
          <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor">
            <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
          </svg>
          <span className={styles.srOnly}>Close</span>
        </button>
      </div>
    </div>
  );
};

export default HowToPlayPopup;
